import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FieldSelect } from "@/components/ui/field-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PLAN_LABEL, SubStatusBadge, eur, fmtDate } from "./adminUtils";
import { Loader2, Search, Building2, UserPlus, Copy, CheckCircle2 } from "lucide-react";

const PLAN_OPTS = [{ value: "all", label: "Tous les plans" }, ...Object.entries(PLAN_LABEL).map(([value, label]) => ({ value, label }))];
const PLAN_OPTS_FORM = Object.entries(PLAN_LABEL).map(([value, label]) => ({ value, label }));
const STATUS_OPTS = [
  { value: "all", label: "Tous les statuts" }, { value: "active", label: "Actif" },
  { value: "trialing", label: "Essai" }, { value: "past_due", label: "Impayé" },
  { value: "cancelled", label: "Résilié" }, { value: "restricted", label: "Restreint (grâce dépassée)" },
];

function CreateAccountModal({ open, onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ org_name: "", owner_name: "", owner_email: "", plan: "pro", delivery: "invite", password: "", city: "", country: "" });
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const create = useMutation({
    mutationFn: async () => (await api.post("/api/admin/accounts", f)).data,
    onSuccess: (d) => { setResult(d); toast.success("Compte créé"); qc.invalidateQueries({ queryKey: ["admin-orgs-list"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  const reset = () => { setF({ org_name: "", owner_name: "", owner_email: "", plan: "pro", delivery: "invite", password: "", city: "", country: "" }); setResult(null); };
  const close = () => { reset(); onClose(); };
  const canSubmit = f.org_name.trim() && f.owner_name.trim() && f.owner_email.trim() && (f.delivery === "invite" || f.password.length >= 8);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="admin-create-account-dialog">
        <DialogHeader><DialogTitle>Créer un compte client (offert)</DialogTitle>
          <DialogDescription>Compte activé sans paiement Stripe. L'abonnement est marqué « actif offert ».</DialogDescription></DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="admin-create-account-result">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Compte créé pour {result.owner_email}</div>
            {result.invite_link ? (
              <div>
                <p className="mb-1 text-xs text-slate-500">Lien d'invitation (l'owner définit son mot de passe) :</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={result.invite_link} data-testid="admin-invite-link" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs" />
                  <button onClick={() => { navigator.clipboard?.writeText(result.invite_link); toast.success("Lien copié"); }} className="rounded-lg border border-slate-300 p-2 hover:bg-slate-50"><Copy className="h-4 w-4" /></button>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Un email d'invitation a aussi été envoyé.</p>
              </div>
            ) : <p className="text-sm text-slate-600">Un email de bienvenue a été envoyé. L'owner se connecte avec le mot de passe que tu as défini.</p>}
            <DialogFooter>
              <button onClick={reset} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Créer un autre</button>
              <button onClick={close} data-testid="admin-create-account-done" className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Terminé</button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <input value={f.org_name} onChange={set("org_name")} placeholder="Nom de l'organisation *" data-testid="ca-org-name" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
            <div className="grid grid-cols-2 gap-3">
              <input value={f.owner_name} onChange={set("owner_name")} placeholder="Nom du propriétaire *" data-testid="ca-owner-name" className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
              <input value={f.owner_email} onChange={set("owner_email")} placeholder="Email du propriétaire *" data-testid="ca-owner-email" className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={f.city} onChange={set("city")} placeholder="Ville" data-testid="ca-city" className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
              <input value={f.country} onChange={set("country")} placeholder="Pays" data-testid="ca-country" className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-slate-500">Plan offert</p>
              <FieldSelect testId="ca-plan" className="w-full" value={f.plan} onChange={set("plan")} options={PLAN_OPTS_FORM} />
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold text-slate-500">Accès du propriétaire</p>
              <div className="flex gap-2">
                <button onClick={() => setF((p) => ({ ...p, delivery: "invite" }))} data-testid="ca-delivery-invite" className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${f.delivery === "invite" ? "border-primary bg-primary/10 text-primary" : "border-slate-300 text-slate-600"}`}>Email d'invitation</button>
                <button onClick={() => setF((p) => ({ ...p, delivery: "password" }))} data-testid="ca-delivery-password" className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${f.delivery === "password" ? "border-primary bg-primary/10 text-primary" : "border-slate-300 text-slate-600"}`}>Mot de passe temporaire</button>
              </div>
            </div>
            {f.delivery === "password" && (
              <input value={f.password} onChange={set("password")} type="text" placeholder="Mot de passe (min 8 caractères)" data-testid="ca-password" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
            )}
            <DialogFooter>
              <button onClick={close} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Annuler</button>
              <button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending} data-testid="ca-submit" className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
                {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Créer le compte</button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminOrganizations() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState("all"); const [status, setStatus] = useState("all");
  const [q, setQ] = useState(""); const [createdFrom, setCreatedFrom] = useState(""); const [page, setPage] = useState(1);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-orgs-list", plan, status, q, createdFrom, includeArchived, page],
    queryFn: async () => (await api.get("/api/admin/orgs", { params: {
      plan: plan === "all" ? undefined : plan, status: status === "all" ? undefined : status,
      q: q || undefined, created_from: createdFrom || undefined,
      include_archived: includeArchived || undefined, page, page_size: pageSize } })).data,
    retry: false, keepPreviousData: true,
  });
  const rows = data?.items || []; const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="admin-organizations">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Organisations</h1>
          <p className="mt-1 text-sm text-slate-500">{total} organisation{total > 1 ? "s" : ""} · statuts, plans et MRR</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="admin-create-account-btn" className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"><UserPlus className="h-4 w-4" /> Créer un compte</button>
      </div>

      <div className="mb-4 mt-5 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Rechercher (nom, email owner)" data-testid="admin-orgs-search"
            className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
        </div>
        <FieldSelect testId="admin-orgs-filter-plan" className="w-40" value={plan} onChange={(v) => { setPlan(v); setPage(1); }} options={PLAN_OPTS} />
        <FieldSelect testId="admin-orgs-filter-status" className="w-52" value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={STATUS_OPTS} />
        <input type="date" value={createdFrom} onChange={(e) => { setCreatedFrom(e.target.value); setPage(1); }} data-testid="admin-orgs-filter-date"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 focus:border-primary focus:outline-none" />
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={includeArchived} onChange={(e) => { setIncludeArchived(e.target.checked); setPage(1); }} data-testid="admin-orgs-include-archived" className="h-4 w-4 rounded border-slate-300" /> Voir archivées
        </label>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="admin-orgs-empty">
          <Building2 className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucune organisation ne correspond à ces filtres.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-orgs-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>{["Organisation", "Owner", "Plan", "Abonnement", "Étab.", "Users", "Inscription", "Activité", "MRR"].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((o) => (
                <tr key={o.id} onClick={() => navigate(`/admin/organisations/${o.id}`)} data-testid={`admin-org-row-${o.id}`} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-3"><p className="font-medium text-slate-800">{o.name}
                    {o.is_demo && <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">Démo</span>}
                    {o.archived && <span className="ml-1.5 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">Archivée</span>}</p></td>
                  <td className="px-4 py-3 text-slate-600">{o.owner_email || "—"}</td>
                  <td className="px-4 py-3 capitalize text-slate-700">{PLAN_LABEL[o.plan] || o.plan || "—"}</td>
                  <td className="px-4 py-3"><SubStatusBadge status={o.subscription_status} />{o.restricted && <span className="ml-1 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-600">Restreint</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{o.locations}</td>
                  <td className="px-4 py-3 text-slate-600">{o.users}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(o.created_at)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(o.last_activity)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-800">{eur(o.mrr_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>{total} organisation{total > 1 ? "s" : ""}</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-orgs-prev" className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40">Précédent</button>
            <span>Page {page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="admin-orgs-next" className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40">Suivant</button>
          </div>
        </div>
      )}

      <CreateAccountModal open={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
}
